<template>
  <div class="admin-reservation">
    <header class="admin-reservation__header">
      <div>
        <p class="eyebrow">Panel interno</p>
        <h2>{{ formTitle }}</h2>
        <p class="subtle">{{ formSubtitle }}</p>
      </div>
      <div class="admin-reservation__badge">
        <span>Modo</span>
        <strong>{{ modeBadge }}</strong>
      </div>
    </header>

    <form class="admin-form" @submit.prevent="makeReservation">
      <section class="form-card">
        <div class="form-card__head">
          <h3>Datos del cliente</h3>
          <p>Registra la información de contacto para confirmar la reserva y trabajar el cupo por hora.</p>
        </div>
        <div class="form-grid">
          <label class="field" for="name">
            <span>Nombre</span>
            <input type="text" v-model="state.reservation.name" id="name" required>
          </label>
          <label class="field" for="email">
            <span>Email</span>
            <input type="email" v-model="state.reservation.email" id="email" required>
          </label>
          <label class="field" for="phone">
            <span>Teléfono</span>
            <input type="tel" v-model="state.reservation.phone" id="phone" required>
          </label>
        </div>
      </section>

      <section class="form-card">
        <div class="form-card__head">
          <h3>Detalle de la reservación</h3>
          <p>{{ detailCopy }}</p>
        </div>
        <div class="form-grid">
          <label class="field" for="date">
            <span>Fecha</span>
            <input type="date" v-model="state.reservation.date" id="date" required @change="checkRules">
          </label>
          <label class="field" for="time">
            <span>Hora</span>
            <select v-model="state.reservation.time" id="time" required>
              <option value="">Selecciona un horario</option>
              <option v-for="time in availableTimes" :key="time" :value="time">{{ formatTime(time) }}</option>
            </select>
          </label>
          <label class="field" for="people">
            <span>Personas</span>
            <input type="number" v-model.number="state.reservation.people" id="people" required min="1" :max="maxPeople">
          </label>
        </div>
        <p class="form-error" v-if="timeError">{{ timeError }}</p>
        <p class="form-hint">{{ effectivePaymentSummary }}</p>
      </section>

      <section class="form-card">
        <div class="form-card__head">
          <h3>Comentarios del cliente</h3>
          <p>Registra alergias, preferencias o notas del cliente.</p>
        </div>
        <div class="form-grid">
          <label class="field field--full" for="specialNotes">
            <span>Notas</span>
            <textarea
              v-model="state.reservation.specialNotes"
              id="specialNotes"
              :disabled="noSpecialNotes"
              placeholder="Ej. Celebración, alergias, silla alta…"
            ></textarea>
          </label>
          <label class="field checkbox" for="noSpecialNotes">
            <input type="checkbox" v-model="noSpecialNotes" id="noSpecialNotes">
            <span>No hay comentarios</span>
          </label>
        </div>
        <p class="form-error" v-if="specialNotesError">{{ specialNotesError }}</p>
      </section>

      <footer class="form-actions">
        <button class="btn-primary" type="submit" :disabled="loading">
          <template v-if="loading">
            <span class="spinner"></span> Procesando...
          </template>
          <template v-else>
            {{ submitLabel }}
          </template>
        </button>
        <p class="form-hint">{{ submitHint }}</p>
      </footer>
    </form>
  </div>
</template>

<script>
import { ref, watch, onMounted, computed } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, email, minLength, minValue } from '@vuelidate/validators';
import { collection, getDocs, query, where, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/init';
import { parseLocalDate } from '../utils/date';
import { formatTimeLabel } from '../utils/time';
import { getAdminISODate } from '../utils/adminPanel';
import { findPaymentOverrideForDate, resolveEffectivePaymentSettings } from '../utils/paymentOverrides';
import { createAdminReservation as createAdminReservationRequest } from '../services/api/adminReservations';

const WEEKDAY_TIME_OPTIONS = [
  '12:00', '12:30', '13:00', '13:30', '14:45',
  '15:00', '15:30', '16:00', '16:30', '17:45',
  '18:00', '18:30', '19:00', '19:30'
];

const SUNDAY_TIME_OPTIONS = [
  '12:00', '12:30', '13:00', '13:30', '14:45',
  '15:00', '15:30', '16:00', '16:30', '17:45'
];

const WEEKEND_DAY_MAP = {
  5: 'friday',
  6: 'saturday',
  0: 'sunday'
};

const createDefaultWeeklyTemplates = () => ({
  friday: [...WEEKDAY_TIME_OPTIONS],
  saturday: [...WEEKDAY_TIME_OPTIONS],
  sunday: [...SUNDAY_TIME_OPTIONS],
  special: [...WEEKDAY_TIME_OPTIONS]
});

const ADMIN_OVERRIDE_BLOCKS = true;
export default {
  name: 'AdminReservationView',
  props: {
    mode: {
      type: String,
      default: 'reservation'
    }
  },
  emits: ['reservation-complete'],
  setup(props, { emit }) {
    const isWalkInMode = computed(() => props.mode === 'walkin');
    const state = ref({
      reservation: {
        name: '',
        email: '',
        phone: '',
        date: isWalkInMode.value ? getAdminISODate() : '',
        time: '',
        people: 1,
        specialNotes: ''
      }
    });

    const maxPeople = ref(50);
    const timeError = ref('');
    const specialNotesError = ref('');
    const noSpecialNotes = ref(false);
    const loading = ref(false);
    const availableTimes = ref([]);
    const disabledTimes = ref({});
    const weeklyTemplates = ref(createDefaultWeeklyTemplates());
    const paymentEnabled = ref(false);
    const paymentMode = ref('deposit');
    const paymentAmount = ref(0);
    const paymentCurrency = ref('USD');
    const paymentOverrides = ref([]);

    const rules = {
      reservation: {
        name: { required },
        email: { required, email },
        phone: { required, minLength: minLength(10) },
        date: { required },
        time: { required },
        people: { required, minValue: minValue(1) },
        specialNotes: { required: () => !noSpecialNotes.value || state.value.reservation.specialNotes.trim() !== '' }
      }
    };

    const v$ = useVuelidate(rules, state);
    const formTitle = computed(() => isWalkInMode.value ? 'Registrar walk-in' : 'Crear reservación');
    const formSubtitle = computed(() => (
      isWalkInMode.value
        ? 'Usa el mismo flujo del admin y trabaja por cupo según cantidad de personas por hora.'
        : 'Formulario exclusivo para el equipo. No es la vista del cliente.'
    ));
    const modeBadge = computed(() => isWalkInMode.value ? 'Walk-in' : 'Admin');
    const detailCopy = computed(() => (
      isWalkInMode.value
        ? 'El walk-in usa el mismo control de horarios y cupo por hora del panel.'
        : 'El cupo se trabaja por cantidad de personas por hora dentro de los horarios habilitados.'
    ));
    const submitLabel = computed(() => isWalkInMode.value ? 'Crear walk-in' : 'Crear reservación');
    const submitHint = computed(() => (
      isWalkInMode.value
        ? 'Se guardará como pendiente hasta confirmar y enviar el QR.'
        : 'Se guardará como pendiente hasta confirmar.'
    ));
    const effectivePaymentSettings = computed(() => resolveEffectivePaymentSettings({
      paymentEnabled: paymentEnabled.value,
      paymentMode: paymentMode.value,
      paymentAmount: paymentAmount.value,
      paymentCurrency: paymentCurrency.value,
      paymentOverrides: paymentOverrides.value
    }, state.value.reservation.date));
    const effectivePaymentAmount = computed(() => Number(effectivePaymentSettings.value.paymentAmount) || 0);
    const appliedPaymentOverride = computed(() => effectivePaymentSettings.value.appliedPaymentOverride || null);
    const effectivePaymentSummary = computed(() => {
      if (!paymentEnabled.value || effectivePaymentAmount.value <= 0) {
        return 'Cobro público desactivado para esta fecha.';
      }

      const formattedAmount = new Intl.NumberFormat('es-PR', {
        style: 'currency',
        currency: paymentCurrency.value || 'USD'
      }).format(effectivePaymentAmount.value / 100);

      if (appliedPaymentOverride.value) {
        return `Cobro público para esta fecha: ${formattedAmount} (${appliedPaymentOverride.value.label}).`;
      }

      return `Cobro público para esta fecha: ${formattedAmount} (tarifa regular).`;
    });

    const clearInvalidSelection = () => {
      if (!state.value.reservation.time) {
        return;
      }
      if (availableTimes.value.includes(state.value.reservation.time)) {
        return;
      }
      state.value.reservation.time = '';
      if (!timeError.value) {
        timeError.value = 'Ese horario ya no tiene cupo. Selecciona otro.';
      }
    };

    const checkRules = () => {
      timeError.value = '';
      availableTimes.value = [];

      const { date } = state.value.reservation;
      if (!date) {
        timeError.value = 'Selecciona una fecha para mostrar horarios.';
        return;
      }

      const selectedDate = parseLocalDate(date);
      const dayIndex = selectedDate ? selectedDate.getDay() : NaN;

      const specialDateRule = findPaymentOverrideForDate(paymentOverrides.value, date);

      if (![5, 6, 0].includes(dayIndex) && !specialDateRule) {
        timeError.value = 'Las reservaciones solo están disponibles de viernes a domingo.';
        return;
      }

      const dayKey = WEEKEND_DAY_MAP[dayIndex];
      const baseTimes = dayKey
        ? weeklyTemplates.value[dayKey] || []
        : weeklyTemplates.value.special || weeklyTemplates.value.friday || WEEKDAY_TIME_OPTIONS;
      if (!baseTimes.length) {
        timeError.value = 'No hay horarios habilitados para este día.';
        return;
      }

      if (ADMIN_OVERRIDE_BLOCKS) {
        availableTimes.value = baseTimes;
      } else {
        const disabled = disabledTimes.value[date] || [];
        availableTimes.value = baseTimes.filter((time) => !disabled.includes(time));
      }

      if (!availableTimes.value.length) {
        timeError.value = 'No hay horarios habilitados para esta fecha.';
        clearInvalidSelection();
        return;
      }

      filterAvailableTimes();
    };

    const filterAvailableTimes = async () => {
      if (!state.value.reservation.date || !availableTimes.value.length) {
        return;
      }
      const reservedTimes = await getReservedTimes(state.value.reservation.date);

      // Asegurarse de que todas las horas estén disponibles mientras no hayan alcanzado las 100 personas
      availableTimes.value = availableTimes.value.filter(time => {
        const totalPeopleForTime = reservedTimes[time] || 0;
        return totalPeopleForTime < 100; // Permitir las horas con menos de 100 personas
      });
      if (!availableTimes.value.length) {
        timeError.value = 'Los horarios para esta fecha alcanzaron el cupo máximo.';
      }
      clearInvalidSelection();
    };

    const getReservedTimes = async (date) => {
      const q = query(collection(db, 'reservations'), where('date', '==', date));
      const querySnapshot = await getDocs(q);

      const reservedTimes = {};

      // Contabilizar el número de personas por hora
      querySnapshot.forEach(doc => {
        const reservation = doc.data();
        if (reservation.type === 'walkin' || reservation.source === 'admin_walkin') {
          return;
        }
        if (reservation.status === 'awaiting_payment' || (reservation.paymentRequired && reservation.paymentStatus === 'awaiting_payment')) {
          return;
        }
        if (!reservedTimes[reservation.time]) {
          reservedTimes[reservation.time] = 0;
        }
        reservedTimes[reservation.time] += Number(reservation.people) || 0; // Sumar la cantidad de personas por hora
      });

      return reservedTimes;
    };


    const makeReservation = async () => {
      v$.value.$touch();
      if (v$.value.$invalid || timeError.value) {
        console.error('Validation failed');
        specialNotesError.value = 'Debe escribir notas especiales o seleccionar la opción "No tengo notas especiales".';
        return;
      }
      loading.value = true;
      try {
        const peopleCount = Number(state.value.reservation.people) || 0;
        const response = await createAdminReservationRequest({
          ...state.value.reservation,
          people: peopleCount,
          source: isWalkInMode.value ? 'admin_walkin' : 'admin_panel'
        });
        alert(response.message || `${isWalkInMode.value ? 'Walk-in' : 'Reservación'} creada con éxito, ID: ${response.reservationId}`);
        state.value.reservation = {
          name: '',
          email: '',
          phone: '',
          date: isWalkInMode.value ? getAdminISODate() : '',
          time: '',
          people: 1,
          specialNotes: '' // Restablecer notas especiales
        };
        noSpecialNotes.value = false;
        v$.value.$reset();
        emit('reservation-complete', response);
      } catch (error) {
        console.error('Error al crear la reservación:', error);
        alert(error.message || `No se pudo crear ${isWalkInMode.value ? 'el walk-in' : 'la reservación'}.`);
      } finally {
        loading.value = false;
      }
    };


    const formatTime = (time) => formatTimeLabel(time);

    onMounted(() => {
      const adminConfigRef = doc(db, 'adminConfig', 'reservations');
      onSnapshot(adminConfigRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          disabledTimes.value = data.disabledTimes || {};
          weeklyTemplates.value = data.weeklyTemplates || createDefaultWeeklyTemplates();
          paymentEnabled.value = Boolean(data.paymentEnabled);
          paymentMode.value = ['deposit', 'full'].includes(data.paymentMode) ? data.paymentMode : 'deposit';
          paymentAmount.value = Number(data.paymentAmount) || 0;
          paymentCurrency.value = String(data.paymentCurrency || 'USD').trim().toUpperCase() || 'USD';
          paymentOverrides.value = Array.isArray(data.paymentOverrides) ? data.paymentOverrides : [];
          if (state.value.reservation.date) {
            checkRules();
          }
        }
      });
    });

    watch(() => state.value.reservation.date, checkRules);
    watch([disabledTimes, weeklyTemplates, paymentOverrides], () => {
      if (state.value.reservation.date) {
        checkRules();
      }
    }, { deep: true });

    return {
      v$,
      state,
      formTitle,
      formSubtitle,
      modeBadge,
      detailCopy,
      submitLabel,
      submitHint,
      maxPeople,
      makeReservation,
      checkRules,
      timeError,
      specialNotesError,
      noSpecialNotes,
      loading,
      availableTimes,
      formatTime,
      effectivePaymentSummary
    };
  }
};
</script>

<style src="./AdminReservationView.css" scoped></style>
